import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { OpportunityEstimateView, SearchBaseEstimateResultDto, SearchEstimateTemplatesResultDto } from '@api-proxies';
import { OpportunityEstimateService } from '@http/clients/opportunity-estimate.service';
import { IUserOrganization } from '@interfaces/user-organization.interface';
import { AuthService } from '@services/auth.service';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    GenericTypeAheadDropdownComponent,
    IconSanitizerService,
    MessageBoxComponent,
    MessageBoxType,
    ModalComponentBase,
    SnackbarNotificationService
} from 'ngx-lib';
import { CustomMenuItemComponent } from '../../custom-menu-item/custom-menu-item.component';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';
import { TypeAheadUuidsComponent } from '../../type-ahead-uuids/type-ahead-uuids.component';

interface IModalCreateEstimateParameters {
    opportunityId: number;
}

@Component({
    selector: 'app-modal-create-estimate',
    templateUrl: './modal-create-estimate.component.html',
    styleUrls: ['./modal-create-estimate.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ...CDK_IMPORTS,
        GenericTypeAheadDropdownComponent,
        MessageBoxComponent,
        TypeAheadUuidsComponent,
        LoadingButtonComponent,
        CustomMenuItemComponent
    ]
})
export class ModalCreateEstimateComponent
    extends ModalComponentBase<IModalCreateEstimateParameters, OpportunityEstimateView>
    implements OnInit
{
    estimateType: 'blank' | 'template' | 'linkToExisting' = 'blank';

    searchText = '';

    selectedTemplate?: SearchEstimateTemplatesResultDto;

    source?: SearchEstimateTemplatesResultDto[];

    selectedTemplateUUID?: SearchBaseEstimateResultDto;

    userOrganization?: IUserOrganization;

    isLoading = false;

    canCreateBlankEstimate?: boolean;

    disabled = true;

    closeIcon: SafeHtml;

    isLoadingTemplates = false;

    message = '';

    messageType: MessageBoxType = null;

    showCannotLinkMessage = false;

    protected override parameters?: IModalCreateEstimateParameters;

    constructor(
        private readonly opportunityEstimateService: OpportunityEstimateService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly authService: AuthService,
        private readonly awaiter: AwaiterService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        super();
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
    }

    async ngOnInit(): Promise<void> {
        this.canCreateBlankEstimate = !this.authService.hasRestrictedAccess();
        if (!this.canCreateBlankEstimate) {
            this.estimateType = 'template';
        }
        await this.onTemplateTextChanged();
        this.changedDisabled();
    }

    async onTemplateTextChanged(): Promise<void> {
        await this.awaiter.awaitAction(
            'Searching base estimate templates...',
            async () => {
                this.source = await this.opportunityEstimateService.searchBaseEstimateTemplates({
                    filterText: this.searchText
                });
            },
            (loading) => (this.isLoadingTemplates = loading)
        );
    }

    onTemplateChange(itemSelected: SearchEstimateTemplatesResultDto): void {
        this.selectedTemplate = itemSelected;
        this.changedDisabled();
    }

    onTemplateUUIDsChange(itemSelected: SearchBaseEstimateResultDto): void {
        this.selectedTemplateUUID = itemSelected;
        this.changedDisabled();
        this.evaluateLinkAbility();
    }

    removeSelectedTemplate() {
        this.selectedTemplate = undefined;
    }

    onEstimateTypeChange() {
        this.changedDisabled();
        this.evaluateLinkAbility();
    }

    async onConfirm(): Promise<void> {
        if (this.estimateType === 'linkToExisting' && this.selectedTemplateUUID) {
            await this.awaiter.awaitAction(
                'Linking Estimate...',
                async () => {
                    try {
                        const baseEstimateId = this.selectedTemplateUUID ? this.selectedTemplateUUID.id : undefined;

                        if (baseEstimateId && this.parameters?.opportunityId) {
                            const response = await this.opportunityEstimateService.linkBaseEstimate({
                                opportunityId: this.parameters.opportunityId,
                                baseEstimateId: baseEstimateId
                            });

                            if (response) {
                                this.snackbarNotificationService.success('Estimate linked successfully.');
                                this.close(response);
                            }
                        }
                    } catch (error) {
                        const message = this.awaiter.processError(error);
                        this.snackbarNotificationService.error(message);
                        this.close(undefined);
                    }
                },
                (loading) => (this.isLoading = loading)
            );
        } else if (this.estimateType === 'blank' || this.estimateType === 'template') {
            await this.crateEstimate();
        }
    }

    async crateEstimate(): Promise<void> {
        await this.awaiter.awaitAction(
            'Creating Opportunity Estimate...',
            async () => {
                if (!this.parameters) return;

                try {
                    const response = await this.opportunityEstimateService.createBaseEstimate({
                        opportunityId: this.parameters?.opportunityId,
                        templateId: this.selectedTemplate?.id
                    });

                    if (response) {
                        this.snackbarNotificationService.success('Opportunity Estimate created successfully.');
                        this.close(response);
                    }
                } catch (error) {
                    const message = this.awaiter.processError(error);
                    this.snackbarNotificationService.error(message);
                    this.close(undefined);
                }
            },
            (loading) => (this.isLoading = loading)
        );
    }

    changedDisabled() {
        this.disabled = !(
            (this.estimateType === 'linkToExisting' &&
                this.selectedTemplateUUID &&
                !this.selectedTemplateUUID.opportunityId &&
                this.selectedTemplateUUID.canLinked) ||
            (this.estimateType === 'template' && this.selectedTemplate) ||
            this.estimateType === 'blank'
        );
    }

    private evaluateLinkAbility(): void {
        if (
            this.estimateType === 'linkToExisting' &&
            this.selectedTemplateUUID &&
            !this.selectedTemplateUUID.canLinked
        ) {
            this.showCannotLinkMessage = true;
            this.message =
                'Linking not allowed: Salespersons must be assigned to both the CRM Opportunity and the Sales Project.';
            this.messageType = 'danger';
        } else {
            this.showCannotLinkMessage = false;
            this.message = '';
        }
    }
}
